import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { database, ref, onValue, off } from '../lib/firebase';
import { GAMES } from '../games/registry';
import { getAvatarById } from './AvatarPicker';
import { Eye, ArrowLeft } from 'lucide-react';

export default function SpectatorView() {
  const { gameId } = useParams<{ gameId: string }>();
  const [gameData, setGameData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!gameId) return;

    const gameRef = ref(database, `games/${gameId}`);
    onValue(gameRef, (snapshot) => {
      if (!snapshot.exists()) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setGameData(snapshot.val());
      setLoading(false);

      // Dispatch state so game component can render
      const state = snapshot.val().state;
      if (state && Object.keys(state).length > 0) {
        window.dispatchEvent(new CustomEvent('game_state', { detail: state }));
      }
    });

    return () => off(gameRef);
  }, [gameId]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500" />
      </div>
    );
  }

  if (notFound || !gameData) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4 text-center">
        <Eye className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h2 className="text-2xl font-bold mb-2">Game Not Found</h2>
        <p className="text-gray-300 mb-6">This game may have ended or doesn't exist.</p>
        <Link to="/watch" className="text-pink-400 hover:text-pink-300 transition flex items-center justify-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to Live Games
        </Link>
      </div>
    );
  }

  const gameDef = GAMES.find(g => g.id === gameData.gameType);
  const GameComponent = gameDef?.component;
  const hostAvatar = getAvatarById(gameData.hostAvatar);
  const guestAvatar = getAvatarById(gameData.guestAvatar);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <Link to="/watch" className="text-gray-300 hover:text-white transition flex items-center gap-2 mb-6 text-sm">
        <ArrowLeft className="w-4 h-4" /> Live Games
      </Link>

      <div className="bg-white/5 backdrop-blur-lg rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Eye className="w-4 h-4 text-pink-400" />
              <span className="text-pink-400 font-medium">Spectating</span>
            </div>
            <span className="text-gray-300">|</span>
            <span className="text-2xl">{gameDef?.icon}</span>
            <span className="font-medium">{gameDef?.name}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1">
              {hostAvatar && <span>{hostAvatar.emoji}</span>}
              {gameData.hostUsername || 'Player 1'}
            </span>
            <span className="text-gray-300">vs</span>
            <span className="flex items-center gap-1">
              {guestAvatar && <span>{guestAvatar.emoji}</span>}
              {gameData.guestUsername || 'Player 2'}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-8 relative">
        {/* Overlay to prevent interaction */}
        <div className="absolute inset-0 z-10 rounded-xl" />
        {GameComponent ? (
          <GameComponent
            onGameEnd={() => {}}
            isHost={true}
            sendGameState={() => {}}
            gameId={gameId || ''}
            isOffline={false}
          />
        ) : (
          <p className="text-center text-gray-300">Unknown game type</p>
        )}
      </div>

      {gameData.status === 'finished' && (
        <div className="mt-4 text-center text-gray-300">
          This game has ended.
        </div>
      )}
    </div>
  );
}
